/**
 * The canonical new-account journey: the five steps from an invitation code to a working workspace.
 *
 * Plain data, so every surface that draws the steps reads ONE list: today's stepper
 * (src/components/onboarding/JourneyStepper.tsx, which re-exports it), today's /pricing chips, onboarding, and the pricing
 * page's rebuild in the new design (which may not import a styled-components module, design-system §7b). Moved here on
 * 2026-09-30; before that it lived inside the stepper's styled file.
 *
 * Keep labels lowercase. Every phase is announced here from the first screen (2026-08-04 owner directive: all
 * expectations set, no surprises), including the two optional guided steps, which happen IN the flow, never on another
 * page.
 *
 * Five steps, no more (task #53, 2026-08-12): the old sixth step, "meet your workspace", was an orientation recap of
 * choices the user had JUST made, removed everywhere; finishing setup opens the workspace directly. Surfaces that know
 * the user deferred an optional step pass a filtered copy of this list so the count shrinks dynamically (Onboarding.tsx).
 */
export interface JourneyStep {
  key: string;
  label: string;
  /** Short time expectation, e.g. '~30 seconds'. Shown for the active step. */
  timeHint?: string;
}

export const NEW_ACCOUNT_JOURNEY: JourneyStep[] = [
  { key: 'code', label: 'invite code', timeHint: '~10 seconds' },
  { key: 'account', label: 'create your account', timeHint: '~1 minute' },
  { key: 'profile', label: 'profile setup', timeHint: '~30 seconds' },
  // R8 (2026-10-30): the plan the evaluation starts when it ends, chosen after the profile.
  { key: 'plan', label: 'your Prest0n team', timeHint: '~20 seconds' },
  { key: 'apps', label: 'connect your apps', timeHint: 'optional · ~30 seconds' },
  { key: 'team', label: 'invite your team', timeHint: 'optional · ~1 minute' },
];

/** A step whose time hint marks it optional (the chips name it). */
export const isOptionalStep = (step: JourneyStep): boolean => !!step.timeHint?.startsWith('optional');

/** Where the person is in the journey ("step 2 of 5"), as every stepper says it. */
export const journeyCounter = (position: number, total: number): string => `step ${position} of ${total}`;
